import { useState, useEffect } from 'react';
import { api } from '@/App';
import Navbar from '@/components/Navbar';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export default function PrivacyPage({ user, onLogout }) {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPrivacy();
  }, []);

  const loadPrivacy = async () => {
    try {
      const response = await api.get('/legal/privacy');
      const defaultContent = `# Privacy Policy

## 1. Information We Collect

We collect information that you provide directly to us when you:

- Register for an account
- Participate in games
- Contact us through our forms
- Update your profile

### Personal Information

- **Name** and contact details
- **Email address** (used as username)
- **Phone number**
- **Date of birth**
- **Volleyball experience** and skill level
- **Profile photo** (optional)

## 2. How We Use Your Information

We use the information we collect to:

- **Manage** your account and membership
- **Organize** volleyball games and events
- **Communicate** with you about games, payments, and updates
- **Balance teams** based on skill level and preferences
- **Send** payment reminders and game notifications
- **Improve** our services

## 3. Data Sharing

We **do not sell** your personal information. We may share your information:

- With other members for game organization purposes
- When required by law or legal process
- With your consent

## 4. Data Security

We implement appropriate security measures to protect your personal information:

- Secure password storage
- Encrypted data transmission
- Limited access to personal information
- Regular security updates

## 5. Your Rights

You have the right to:

- **Access** your personal data
- **Update** or correct your information
- **Delete** your account
- **Withdraw** consent at any time
- **Export** your data

## 6. GDPR Compliance

In accordance with GDPR, we:

- Process data lawfully and transparently
- Collect data only for specified purposes
- Minimize data collection
- Ensure data accuracy
- Limit data retention
- Maintain data security

## 7. Cookies and Tracking

We use essential cookies to:

- Keep you logged in
- Remember your preferences
- Improve website functionality

## 8. Data Retention

We retain your information:

- While your account is active
- As required by law
- For up to 1 year after account deletion (for legal purposes)

## 9. Children's Privacy

Our services are not intended for users under 18 without parental consent.

## 10. Changes to This Policy

We may update this privacy policy from time to time. We will notify you of significant changes via email or website notification.

## 11. Contact Us

If you have questions about this privacy policy or how we handle your data, please contact us through our contact form.

---

*Last updated: ${new Date().toLocaleDateString()}*`;
      
      setContent(response.data.content || defaultContent);
    } catch (error) {
      console.error('Failed to load privacy policy:', error);
      setContent('# Privacy Policy\n\nFailed to load privacy policy.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50">
      {user && <Navbar user={user} onLogout={onLogout} />}
      
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading...</p>
            </div>
          ) : (
            <div className="legal-content">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({node, ...props}) => <h1 className="text-4xl font-bold text-gray-900 mb-6" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3" {...props} />,
                  p: ({node, ...props}) => <p className="text-gray-700 leading-relaxed mb-4" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc pl-6 mb-4 text-gray-700" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal pl-6 mb-4 text-gray-700" {...props} />,
                  li: ({node, ...props}) => <li className="mb-2" {...props} />,
                  strong: ({node, ...props}) => <strong className="font-bold text-orange-600" {...props} />,
                  hr: ({node, ...props}) => <hr className="my-8 border-gray-300" {...props} />,
                  a: ({node, ...props}) => <a className="text-orange-600 underline hover:text-orange-700" {...props} />
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
